import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo.jsx';

const SESSION_KEY = 'cv.adminSession';

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [inputs, setInputs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // The password is kept for the session because every admin-update action
    // (get/save) is re-validated server-side with it.
    const saved = window.sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      setPassword(saved);
      setAuthed(true);
    }
  }, []);

  useEffect(() => {
    if (!authed || !password) return;
    // Load the last-saved inputs from the blob; fall back to the bundled seed
    // file (pre-blob state), then to empty defaults.
    fetch('/api/admin-update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'get', password })
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data?.inputs || fetchSeedInputs())
      .catch(() => fetchSeedInputs())
      .then(setInputs);
  }, [authed, password]);

  function handleLogin(e) {
    e.preventDefault();
    setError(null);
    fetch('/api/admin-update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'auth', password })
    })
      .then((r) => {
        if (!r.ok) throw new Error('Wrong password');
        window.sessionStorage.setItem(SESSION_KEY, password);
        setAuthed(true);
      })
      .catch((e) => setError(e.message));
  }

  function handleSave() {
    setSaving(true);
    setError(null);
    fetch('/api/admin-update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'save', password, inputs })
    })
      .then((r) => {
        if (!r.ok) throw new Error('Save failed');
        setSavedAt(new Date().toLocaleTimeString());
      })
      .catch((e) => setError(e.message))
      .finally(() => setSaving(false));
  }

  if (!authed) {
    return (
      <div className="mx-auto max-w-sm px-4 pt-16">
        <Seo title="Admin — Charleston.Vacations" noindex />
        <Link to="/" className="font-display text-lg font-semibold text-navy-900 no-underline">
          Charleston<span className="text-cream-500">.</span>Vacations
        </Link>
        <h1 className="mt-8 font-display text-2xl font-semibold text-navy-900">Admin</h1>
        <form onSubmit={handleLogin} className="mt-6 space-y-3">
          <input
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-navy-200 bg-white px-3 py-2 text-navy-900 focus:border-navy-600 focus:outline-none"
          />
          <button
            type="submit"
            className="w-full rounded-lg bg-navy-900 px-4 py-2 font-medium text-cream-50 hover:bg-navy-800"
          >
            Sign in
          </button>
          {error && <p className="text-sm text-charlestonpink-600">{error}</p>}
        </form>
      </div>
    );
  }

  if (!inputs) return <p className="px-4 pt-16 text-navy-500">Loading…</p>;

  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6">
      <Seo title="Admin — Charleston.Vacations" noindex />
      <Link to="/" className="font-display text-lg font-semibold text-navy-900 no-underline">
        Charleston<span className="text-cream-500">.</span>Vacations
      </Link>
      <h1 className="mt-6 font-display text-3xl font-semibold text-navy-900">Operator inputs</h1>
      <p className="mt-1 text-sm text-navy-500">
        Saving rebuilds the site. Changes are live within ~2 minutes.
      </p>

      <BeachFlagsEditor inputs={inputs} setInputs={setInputs} />
      <EventsEditor inputs={inputs} setInputs={setInputs} />
      <CruiseShipsEditor inputs={inputs} setInputs={setInputs} />
      <DiningFlagsEditor inputs={inputs} setInputs={setInputs} />
      <FunnelPanel password={password} />

      <div className="mt-8 flex items-center gap-3 border-t border-navy-100 pt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-lg bg-navy-900 px-5 py-2 font-medium text-cream-50 hover:bg-navy-800 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save and rebuild'}
        </button>
        {savedAt && <span className="text-sm text-marsh-600">Saved at {savedAt}</span>}
        {error && <span className="text-sm text-charlestonpink-600">{error}</span>}
      </div>
    </div>
  );
}

function fetchSeedInputs() {
  return fetch('/data/manual-inputs.json', { cache: 'no-cache' })
    .then((r) => (r.ok ? r.json() : defaultInputs()))
    .catch(() => defaultInputs());
}

function defaultInputs() {
  return {
    lastUpdated: new Date().toISOString(),
    beachFlags: {
      folly: { flag: 'green', note: '' },
      iop: { flag: 'green', note: '' },
      sullivans: { flag: 'green', note: '' },
      kiawah: { flag: 'green', note: '' }
    },
    tonightEvents: [],
    cruiseShips: [],
    diningFlags: []
  };
}

function BeachFlagsEditor({ inputs, setInputs }) {
  const beaches = ['folly', 'iop', 'sullivans', 'kiawah'];
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-semibold text-navy-900">Beach flags</h2>
      <div className="mt-3 space-y-3">
        {beaches.map((b) => (
          <div key={b} className="flex flex-wrap items-center gap-3">
            <span className="w-20 text-sm font-medium uppercase text-navy-700">{b}</span>
            <select
              value={inputs.beachFlags?.[b]?.flag || 'green'}
              onChange={(e) =>
                setInputs({
                  ...inputs,
                  beachFlags: {
                    ...inputs.beachFlags,
                    [b]: { ...inputs.beachFlags?.[b], flag: e.target.value }
                  }
                })
              }
              className="rounded-lg border border-navy-200 bg-white px-2 py-1.5 text-sm"
            >
              <option value="green">Green</option>
              <option value="yellow">Yellow</option>
              <option value="red">Red</option>
              <option value="purple">Purple</option>
            </select>
            <input
              type="text"
              placeholder="Note (optional)"
              value={inputs.beachFlags?.[b]?.note || ''}
              onChange={(e) =>
                setInputs({
                  ...inputs,
                  beachFlags: {
                    ...inputs.beachFlags,
                    [b]: { ...inputs.beachFlags?.[b], note: e.target.value }
                  }
                })
              }
              className="flex-1 min-w-[12rem] rounded-lg border border-navy-200 bg-white px-3 py-1.5 text-sm"
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function EventsEditor({ inputs, setInputs }) {
  const events = inputs.tonightEvents || [];
  function addEvent() {
    setInputs({
      ...inputs,
      tonightEvents: [...events, { name: '', venue: '', time: '', blurb: '', priority: 'normal' }]
    });
  }
  function updateEvent(i, key, value) {
    const next = events.slice();
    next[i] = { ...next[i], [key]: value };
    setInputs({ ...inputs, tonightEvents: next });
  }
  function removeEvent(i) {
    setInputs({ ...inputs, tonightEvents: events.filter((_, idx) => idx !== i) });
  }
  return (
    <section className="mt-8">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-navy-900">Tonight events</h2>
        <button onClick={addEvent} className="text-sm font-medium text-navy-700 hover:text-navy-900">
          + Add
        </button>
      </div>
      <div className="mt-3 space-y-4">
        {events.map((ev, i) => (
          <div key={i} className="rounded-lg border border-navy-100 p-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input placeholder="Event name" value={ev.name} onChange={(e) => updateEvent(i, 'name', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm" />
              <input placeholder="Venue" value={ev.venue} onChange={(e) => updateEvent(i, 'venue', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm" />
              <input placeholder="Time" value={ev.time} onChange={(e) => updateEvent(i, 'time', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm" />
              <select value={ev.priority || 'normal'} onChange={(e) => updateEvent(i, 'priority', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm">
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>
            <textarea placeholder="Blurb" value={ev.blurb} onChange={(e) => updateEvent(i, 'blurb', e.target.value)} className="mt-2 w-full rounded border border-navy-200 px-2 py-1 text-sm" rows={2} />
            <button onClick={() => removeEvent(i)} className="mt-1 text-xs text-charlestonpink-600">Remove</button>
          </div>
        ))}
      </div>
    </section>
  );
}

function CruiseShipsEditor({ inputs, setInputs }) {
  const ships = inputs.cruiseShips || [];
  function add() {
    setInputs({
      ...inputs,
      cruiseShips: [...ships, { date: '', ship: '', passengers: 0, in: '', out: '' }]
    });
  }
  function update(i, key, value) {
    const next = ships.slice();
    next[i] = { ...next[i], [key]: value };
    setInputs({ ...inputs, cruiseShips: next });
  }
  function remove(i) {
    setInputs({ ...inputs, cruiseShips: ships.filter((_, idx) => idx !== i) });
  }
  return (
    <section className="mt-8">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-navy-900">Cruise ships</h2>
        <button onClick={add} className="text-sm font-medium text-navy-700 hover:text-navy-900">+ Add</button>
      </div>
      <div className="mt-3 space-y-3">
        {ships.map((s, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <input placeholder="YYYY-MM-DD" value={s.date} onChange={(e) => update(i, 'date', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm" />
            <input placeholder="Ship" value={s.ship} onChange={(e) => update(i, 'ship', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm" />
            <input placeholder="Pax" type="number" value={s.passengers} onChange={(e) => update(i, 'passengers', Number(e.target.value))} className="rounded border border-navy-200 px-2 py-1 text-sm" />
            <input placeholder="In" value={s.in} onChange={(e) => update(i, 'in', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm" />
            <input placeholder="Out" value={s.out} onChange={(e) => update(i, 'out', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm" />
            <button onClick={() => remove(i)} className="col-span-2 text-xs text-charlestonpink-600 sm:col-span-5 sm:text-left">Remove</button>
          </div>
        ))}
      </div>
    </section>
  );
}

function DiningFlagsEditor({ inputs, setInputs }) {
  const flags = inputs.diningFlags || [];
  function add() {
    setInputs({ ...inputs, diningFlags: [...flags, { name: '', status: 'closed', reason: '' }] });
  }
  function update(i, key, value) {
    const next = flags.slice();
    next[i] = { ...next[i], [key]: value };
    setInputs({ ...inputs, diningFlags: next });
  }
  function remove(i) {
    setInputs({ ...inputs, diningFlags: flags.filter((_, idx) => idx !== i) });
  }
  return (
    <section className="mt-8">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-navy-900">Dining flags</h2>
        <button onClick={add} className="text-sm font-medium text-navy-700 hover:text-navy-900">+ Add</button>
      </div>
      <div className="mt-3 space-y-3">
        {flags.map((f, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <input placeholder="Restaurant" value={f.name} onChange={(e) => update(i, 'name', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm" />
            <select value={f.status} onChange={(e) => update(i, 'status', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm">
              <option value="closed">Closed</option>
              <option value="fully_booked">Fully booked</option>
              <option value="vacation">On vacation</option>
            </select>
            <input placeholder="Reason" value={f.reason} onChange={(e) => update(i, 'reason', e.target.value)} className="rounded border border-navy-200 px-2 py-1 text-sm" />
            <button onClick={() => remove(i)} className="text-xs text-charlestonpink-600 sm:col-span-3 sm:text-left">Remove</button>
          </div>
        ))}
      </div>
    </section>
  );
}

// Usage and booking-funnel counts from /api/track (first-party, cookieless).
// Read-only; the numbers answer "has a human ever asked for a quote?".
const FUNNEL_LABELS = {
  page_view: 'Page views (all routes)',
  dates_selected: 'Dates picked on a stay page',
  quote_returned: 'Quotes returned',
  quote_failed: 'Quotes failed',
  book_direct_click: 'Book direct clicked',
  checkout_started: 'Sent to payment page',
  checkout_failed: 'Payment handoff failed',
  booking_confirmed: 'Returned as confirmed',
  subscribe: 'Brief subscriptions'
};

function sumRange(days, from, to) {
  const out = {};
  for (const day of days.slice(from, to)) {
    for (const [e, c] of Object.entries(day.counts || {})) out[e] = (out[e] || 0) + c;
  }
  return out;
}

function FunnelPanel({ password }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!password) return;
    fetch('/api/admin-update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'funnel', password, days: 30 })
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`funnel ${r.status}`))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [password]);

  const today = data ? sumRange(data.days, 0, 1) : {};
  const week = data ? sumRange(data.days, 0, 7) : {};
  const month = data ? data.totals : {};
  const order = data?.order || Object.keys(FUNNEL_LABELS);

  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-semibold text-navy-900">Funnel</h2>
      <p className="mt-1 text-sm text-navy-500">
        Counted by the site itself, no cookies. Bots that run no JavaScript never appear here.
      </p>
      {error && <p className="mt-2 text-sm text-charlestonpink-600">{error}</p>}
      {!data && !error && <p className="mt-2 text-sm text-navy-500">Loading…</p>}
      {data && (
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-navy-500">
              <th className="py-1 font-medium">Step</th>
              <th className="py-1 text-right font-medium">Today</th>
              <th className="py-1 text-right font-medium">7 days</th>
              <th className="py-1 text-right font-medium">30 days</th>
            </tr>
          </thead>
          <tbody>
            {order.map((e) => (
              <tr key={e} className="border-t border-navy-100">
                <td className="py-1 text-navy-800">{FUNNEL_LABELS[e] || e}</td>
                <td className="py-1 text-right tabular-nums text-navy-900">{today[e] || 0}</td>
                <td className="py-1 text-right tabular-nums text-navy-900">{week[e] || 0}</td>
                <td className="py-1 text-right tabular-nums text-navy-900">{month[e] || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
